import { Component, EventEmitter, Input, OnInit, Output, Self } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { AttributeOverrideRow } from 'src/app/models/attribute-override-row';
import { FailedAutoLinkAction } from 'src/app/models/enums/failed-auto-link-action';
import { IProcessDefinition } from 'src/app/models/interfaces/process-definition.interface';
import { OverridedAttribute } from 'src/app/models/overrided-attribute';
import { ProcessDefinitionFormContext } from '../../process-definition-form-context';
import { AttributeOverrideTableComponent } from './attribute-override-table/attribute-override-table.component';
import { AutoLinkFormData } from './models/auto-link-form-data';

import { MatFormFieldModule } from '@angular/material/form-field';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { FormFocusDirective } from '../../../../controls/directives/form-focus.directive';

@Component({
    selector: 'app-auto-link-definition',
    templateUrl: './auto-link-definition.component.html',
    styleUrls: ['./auto-link-definition.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        FaIconComponent,
        MatTooltipModule,
        AttributeOverrideTableComponent,
        RichToolTipDirective,
    ]
})
export class AutoLinkDefinitionComponent implements OnInit, ControlValueAccessor {
    public faCircleInfo = faCircleInfo;

    @Input()
    public processDefinition: Required<IProcessDefinition> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: ProcessDefinitionFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<ProcessDefinitionFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: ProcessDefinitionFormContext) {
        this._context = value;
        this._context$.next(value);
    }

    /**
     * Formulaire contenant les champs.
     */
    autoLinkForm: UntypedFormGroup;

    public actions = [];

    /** Données du formulaire de rattachement automatique */
    private _autoLinkFormData: AutoLinkFormData = {
        allowedStateIds: null,
        attributeId: null,
        failedAutoLinkAction: null,
        overridedAttributes: null,
        postLinkStateId: null,
    };

    /** Setter permettant de spécifier les données du formulaire de rattachement automatique.*/
    @Input() set autoLinkFormData(value: AutoLinkFormData) {
        this._autoLinkFormData = value;
    }

    /** Getter permettant de récupérer les surcharges d'attributs */
    get autoLinkFormData(): AutoLinkFormData {
        return this._autoLinkFormData;
    }

    /** Emis lorsque les données du formulaire de rattachement automatique sont modifiées. */
    @Output() datasChanged = new EventEmitter<AutoLinkFormData>();

    onTouch: () => void;

    onChange: (value?: AutoLinkFormData) => void;

    constructor(@Self() private controlDirective: NgControl) {
        controlDirective.valueAccessor = this;

        this.autoLinkForm = new UntypedFormGroup({});
        this.autoLinkForm.addControl('linkAttribute', new UntypedFormControl(null, Validators.required));
        this.autoLinkForm.addControl('allowedStates', new UntypedFormControl(null, Validators.required));
        this.autoLinkForm.addControl('postLinkState', new UntypedFormControl());
        this.autoLinkForm.addControl('failAction', new UntypedFormControl(null, Validators.required));
        this.autoLinkForm.addControl('attributesOverride', new UntypedFormControl());
    }

    writeValue(autoLinkFormData: AutoLinkFormData): void {
        console.log('AutoLinkComponent writeValue', autoLinkFormData);

        this.autoLinkFormData = autoLinkFormData
            ? autoLinkFormData
            : {
                  allowedStateIds: null,
                  attributeId: null,
                  failedAutoLinkAction: null,
                  overridedAttributes: null,
                  postLinkStateId: null,
              };
        this.autoLinkForm.get('linkAttribute').setValue(this.autoLinkFormData.attributeId);
        this.autoLinkForm.get('allowedStates').setValue(this.autoLinkFormData.allowedStateIds);

        if (this.autoLinkFormData.postLinkStateId === null) {
            this.autoLinkForm.get('postLinkState').setValue(-1);
        } else {
            this.autoLinkForm.get('postLinkState').setValue(this.autoLinkFormData.postLinkStateId);
        }

        this.autoLinkForm.get('failAction').setValue(this.autoLinkFormData.failedAutoLinkAction);

        let overrideAttributes: AttributeOverrideRow[] = [];

        if (this.autoLinkFormData.overridedAttributes) {
            this.autoLinkFormData.overridedAttributes.forEach((x) => {
                let overrideAttribute = new AttributeOverrideRow();
                overrideAttribute.attributeId = x.attributeId;
                overrideAttribute.attributeName = this._context.attributes.find((y) => y.id == x.attributeId).header;
                overrideAttribute.overrideCondition = x.condition;
                overrideAttributes.push(overrideAttribute);
            });
        }

        this.autoLinkForm.get('attributesOverride').setValue(overrideAttributes);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.autoLinkForm.get('linkAttribute').disable();
            this.autoLinkForm.get('allowedStates').disable();
            this.autoLinkForm.get('postLinkState').disable();
            this.autoLinkForm.get('failAction').disable();
            this.autoLinkForm.get('attributesOverride').disable();
        } else {
            this.autoLinkForm.get('linkAttribute').enable();
            this.autoLinkForm.get('allowedStates').enable();
            this.autoLinkForm.get('postLinkState').enable();
            this.autoLinkForm.get('failAction').enable();
            this.autoLinkForm.get('attributesOverride').enable();
        }
    }

    ngOnInit(): void {
        // this.autoLinkForm = new FormGroup({});
        // this.autoLinkForm.addControl("linkAttribute", new FormControl(null, Validators.required));
        // this.autoLinkForm.addControl("allowedStates", new FormControl());
        // this.autoLinkForm.addControl("postLinkState", new FormControl());
        // this.autoLinkForm.addControl("failAction", new FormControl());
        // this.autoLinkForm.addControl("attributesOverride", new FormControl());

        this.controlDirective.control.setValidators([this.validate.bind(this)]);
        this.controlDirective.control.updateValueAndValidity();

        this.actions = [];
        this.actions.push({
            value: FailedAutoLinkAction.FolderCreation,
            text: "Création d'un nouveau dossier",
        });
        this.actions.push({
            value: FailedAutoLinkAction.RedirectToRejectBox,
            text: 'Redirection vers la bannette de rejet',
        });

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
        });
    }

    onAttributesChanged(attributeOverrideRows: AttributeOverrideRow[]) {
        //this.autoLinkForm.get('attributesOverride').updateValueAndValidity();
        let overridedAttributes: OverridedAttribute[] = [];

        attributeOverrideRows.forEach((x) => {
            let overridedAttribute = new OverridedAttribute();
            overridedAttribute.attributeId = x.attributeId;
            overridedAttribute.condition = x.overrideCondition;
            overridedAttributes.push(overridedAttribute);
        });

        this.autoLinkFormData.overridedAttributes = overridedAttributes;
        this.applyChange();
    }

    private applyChange(): void {
        this.datasChanged.emit(this.autoLinkFormData);

        if (this.onChange) {
            this.onChange(this.autoLinkFormData);
        }
    }

    public onLinkAttributeChanged(event: MatSelectChange) {
        console.log('AutoLinkComponent - onLinkAttributeChanged', event);
        this.autoLinkFormData.attributeId = event.value;
        this.applyChange();
    }

    public onAllowStatesChanged(event: MatSelectChange) {
        console.log('AutoLinkComponent - onAllowStatesChanged', event);
        this.autoLinkFormData.allowedStateIds = event.value;
        this.applyChange();
    }

    public onPostLinkStateChanged(event: MatSelectChange) {
        console.log('AutoLinkComponent - onPostLinkStateChanged', event);
        if (event.value !== -1) {
            this.autoLinkFormData.postLinkStateId = event.value;
        } else {
            this.autoLinkFormData.postLinkStateId = null;
        }
        this.applyChange();
    }

    public onFailActionChanged(event: MatSelectChange) {
        console.log('AutoLinkComponent - onFailActionChanged', event);
        this.autoLinkFormData.failedAutoLinkAction = event.value;
        this.applyChange();
    }

    validate({ value }: UntypedFormControl) {
        const isNotValid = this.autoLinkForm.get('linkAttribute').invalid || this.autoLinkForm.get('allowedStates').invalid || this.autoLinkForm.get('failAction').invalid;
        return (
            isNotValid && {
                invalid: true,
            }
        );
    }
}
